import { Link } from '@reach/router';
import { Collection } from 'shopify-storefront-api-typings';
import styled, { css } from 'styled-components';
import { withQuery } from '../data/withQuery';
import { Metafields, Product} from '@/types/ecommerce.types';
import { getCurrentVariant } from '@/utils/utils';
import { ProductOptionsTitle } from './ProductOptions.styles';
import { colors } from '@/themes/colorsMapping';
import getProductDescription from '@/utils/getProductDescription';

const StyledLink = styled(Link)<{ $availableToSell?: boolean }>`
  ${(props) =>
    !props.$availableToSell &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
      display: block;
    `}
  span {
    padding: 5px 10px;
    display: inline-block;
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
  }
`;
StyledLink.displayName = 'ColorSwatch';

const SwatchesList = styled.ul`
  padding: 3px;
  margin-top: 13px;
  position: relative;
  display: grid;
  list-style-type: none;
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  gap: 5px;

  @media (min-width: 800px) {
    margin-top: 10px;
  }

  li {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${colors.darkGray};
    &.tmplt-product__connected-collection-item--current {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px var(--color-primary-20);
    }
  }
  a {
    background-color: transparent !important;
    background: none !important;
    display: flex;
    flex-direction: column;
    gap: 0;
    span {
      @media (max-width: 800px) {
        display: block;
        margin-top: -10px;
      }
    }
  }
  img {
    width: 100%;
    height: 70px;
    object-position: bottom;
    object-fit: cover;
  }
  .placeholder-img {
    height: 70px;
    width: 100%;
    background-color: #f1f0ec;
    background: linear-gradient(to right, #f1f0ec 0%, #ffffff 50%, #f1f0ec 100%);
    background-size: 800px 100%;
    animation: shimmer 1.5s infinite linear;
  }
  @keyframes shimmer {
    0% {
      background-position: -800px 0;
    }
    100% {
      background-position: 800px 0;
    }
  }
`;

export type Color = {
  title: string;
  handle: string;
  color: string;
  colorName: string;
  availableToSell: boolean;
};

type PropTypes = {
  product: Product;
  className: string;
  pagePath?: string;
  showDefaultColor?: boolean;
  sectionTitle?: string;
  shopMetafields: Metafields;
  productColorVariants: object;
  productColorLength: number;
};

type VariantColors = {
  colorName: string;
};

type VariantData = {
  id?: string;
  frameSize?: Collection;
  printSize?: string;
  color?: string;
  availableForSale?: boolean;
};

type ColorImageData = {
  image?: string;
  color?: string;
};

const ProductVariantColors = ({
  product,
  className,
  pagePath = '/products',
  showDefaultColor,
  sectionTitle,
  shopMetafields,
  productColorLength,
}: PropTypes) => {

  if (!product || productColorLength <= 1 || !showDefaultColor) {
    return null;
  }

  const variants = product?.variants?.edges;
  const currentVariant = getCurrentVariant(variants);

  if (!currentVariant) {
    return null;
  }

  const currentFrameSize = currentVariant?.selectedOptions.find(option => option.name === 'Frame Size');
  const currentPrintSize = currentVariant?.selectedOptions.find(option => option.name === 'Print Size');
  const currentColor = currentVariant?.selectedOptions.find(option => option.name === 'Color');
  
  const productOptionColors = product?.options.find(option => option.name === 'Color');
  
  const variantColors: VariantColors[] =
    productOptionColors?.values?.map((color: any) => {
      return {
        colorName: color,
      };
    }) || [];


  const variantData: VariantData[] =
    variants?.map((item: any) => {
      const varID = item.node.id;
      const varIdNumb = varID.split('ProductVariant/')
      const frameSize = item?.node?.selectedOptions.find(option => option.name === 'Frame Size');
      const printSize = item?.node?.selectedOptions.find(option => option.name === 'Print Size');
      const color = item?.node?.selectedOptions.find(option => option.name === 'Color');

      return {
        id: varIdNumb[1],
        frameSize: frameSize?.value,
        printSize: printSize?.value,        
        color: color?.value,
        availableForSale: item.node.availableForSale,
      };
    }) || [];

  const colorImages: ColorImageData[] =
    product.product_color_images?.references?.edges?.map((item: any) => {
      const colorName = item?.node?.color_name?.value;
      const image = item?.node?.corner_image?.reference?.image?.url;

      return {
        image: image,
        color: colorName,
      };
    }) || [];

  const { color: productColor } = getProductDescription(product, shopMetafields);

  const getColorItem = (colorName: any) => {
    if (!colorName) return [];

    return variantData.flatMap(item => {
      if (colorName === item?.color && currentFrameSize?.value === item?.frameSize && currentPrintSize?.value === item?.printSize) {      
        return item
      }
      return []; 
    });
  };

  const getColorImage = (colorName: any) => {
    if (!colorName) return [];

    return colorImages.flatMap(item => {
      if (colorName.toLowerCase() === item?.color) {      
        return item
      }
      return []; 
    });
  };

  return (
    <div className={className}>
      {sectionTitle && (
        <ProductOptionsTitle>
          {product.productType === 'Poster' ? 'Finish' : sectionTitle}: {'\u00A0'}
          <span>{productColor}</span>
        </ProductOptionsTitle>
      )}
      <SwatchesList>
        {variantColors?.map((color, index) => {
            const colorName = color?.colorName;
            const colorItem = getColorItem(colorName);
            const colorImage = getColorImage(colorName);
            const url = `${pagePath}/${product?.handle}?variant=${colorItem[0]?.id}`;
            const imageUrl = colorImage[0]?.image;

            return (
                <li
                    key={url}
                    className={`product-option-color-item ${
                    currentColor?.value === colorItem[0].color
                        ? 'tmplt-product__connected-collection-item--current'
                        : ''
                    }`}
                >
                    <StyledLink
                    $availableToSell={colorItem[0]?.availableForSale}
                    to={url}
                    onClick={(e) => {
                        if (!colorItem[0]?.availableForSale) e.preventDefault();
                    }}
                    className={`color-${color?.colorName.toLowerCase()}${
                        !colorItem[0]?.availableForSale ? ' tmplt-product__variants-item--sold-out' : ''
                    }`}
                    style={{ backgroundColor: color?.color }}
                    title={color.colorName}
                    >
                    {imageUrl ? <img src={imageUrl} alt="" /> : <div className="placeholder-img"></div>}
                    <span>{color.colorName}</span>
                    </StyledLink>
                </li>                  
            );
        })}
      </SwatchesList>
    </div>
  );
};

ProductVariantColors.displayName = 'ProductVariantColors';
export default withQuery(ProductVariantColors);
